import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ShoppingCart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import configs from '../config/config';
import CartSection from './CartSection';
import SearchWidget from './SearchWidget';
import BottomBar from '../components/BottomBar'; 
import CategoriesNav from '../components/CategoriesNav'; 
import { useCart } from '../context/CartContext';

// ─── SVG Icon Component ───────────────────────────────────────────────────────
const Icon = ({ name, size = 24, color = "currentColor" }) => {
  const attrs = { fill: "none", stroke: color, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
  const paths = {
    menu:     <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>,
    x:        <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>,
    search:   <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>,
    user:     <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
    bag:      <><ShoppingCart/></>,
  };

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...attrs}>
      {paths[name] || null}
    </svg>
  );
};

// ─── Default config (fallback) ────────────────────────────────────────────────
const DEFAULT_CONFIG = {
  topBar: {
    visible: true,
    gradient: ["#eb5524", "#dd7346"],
    leftMain: "We offer fast and reliable shipping",
    leftSub: "Mon–Sun, 9am–6pm",
    currencies: ["We deliver to:", "Nakuru", "Nairobi", "Mombasa"],
  },
  logo: {
    type: "image",
    text: "Junisa",
    fontSize: 24,
    fontWeight: "700",
    color: "#111827",
    imageUrl: "data:image/jpeg;base64,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",
    width: 120,
    height: 50,
  },
  search: {
    visible: true,
    placeholder: "Search products…",
    borderColor: "#d1d5db",
    borderRadius: 8,
    focusColor: "#2563eb",
    bgColor: "#f3f4f6",
    iconColor: "#6b7280",
    width: "300px",
  },
  menuItems: [
    { id: "home", label: "Home", href: "", position: "left", bold: false },
    { id: "shop", label: "Shop", href: "shop", position: "left", bold: false },
    { id: "help", label: "Help Center", href: "help-center", position: "left", bold: false },
    { id: "contact", label: "Contact", href: "contact", position: "right", bold: false },
  ],
  nav: {
    bg: "#ffffff",
    shadow: false,
    sticky: true,
    height: 64,
    borderBottom: true,
    borderColor: "#e5e7eb",
  },
  icons: { color: "#111827", size: 24 },
  cartBadgeColor: "#2563eb",
  menuFontSize: 15,
  menuTextColor: "#111827",
  menuPosition: "left",
};

// Cache constants
const CACHE_KEY_NAVBAR = 'navbar_config_cache';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

// ─── Main Navigation Bar Component ────────────────────────────────────────────
const NavigationBar = ({ cartConfig, searchConfig,CatConfig, previewConfig = null, previewMode = null }) => {
  const { cartItems } = useCart();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [cfg, setCfg] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchShow,setSearchShow]=useState(false);
  const [user, setUser] = useState(null);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [_cartCount, setCartCount] = useState(0);
  const [allCategories, setCategories] = useState([]);
  const [popularProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredSuggestions, setSuggestions] = useState([]);
  const userInfo = localStorage.getItem('userInfor');
//console.log(userInfo);

 const checkIfReady=(element_)=>{
  if(element_.length>1){
    setSearchShow(true);
    handleSearch(searchQuery);
    //console.log(element_);
  }else{
       setSearchShow(false);
  }
 }
  const userButtonRef = useRef(null);
  const dropdownRef = useRef(null);

  const isPreview = !!previewConfig;
  const effectiveConfig = isPreview ? { ...DEFAULT_CONFIG, ...previewConfig } : cfg;

  const isMobileView = previewMode === "mobile" || 
    (!previewMode && typeof window !== 'undefined' && window.innerWidth <= 768);

  // ── Stale-While-Revalidate Cache Logic for Navbar ─────────────────────────
  useEffect(() => {
    if (isPreview) {
      setCfg({ ...DEFAULT_CONFIG, ...previewConfig });
      setLoading(false);
      return;
    }

    const loadNavbar = async () => {
      // 1. Load cached data first (instant render)
      const cachedStr = localStorage.getItem(CACHE_KEY_NAVBAR);
      let hasStaleCache = false;
      let staleTs = 0;

      if (cachedStr) {
        try {
          const parsed = JSON.parse(cachedStr);
          const { data, ts } = parsed;
          staleTs = ts || 0;
          hasStaleCache = true;

          setCfg(prev => ({
            ...prev,
            ...data,
            topBar: { ...prev.topBar, ...data.topBar },
            logo: { ...prev.logo, ...data.logo },
            search: { ...prev.search, ...data.search },
            nav: { ...prev.nav, ...data.nav },
            icons: { ...prev.icons, ...data.icons },
            menuItems: data.menuItems?.map(i => ({ 
              ...i, 
              id: i.id || Math.random().toString(36).slice(2) 
            })) ?? prev.menuItems,
            menuPosition: data.menuPosition || "left",
          }));
        } catch (e) {
          console.warn("Invalid navbar cache");
          localStorage.removeItem(CACHE_KEY_NAVBAR);
        }
      }

      if (!token) {
        setLoading(false);
        return;
      }

      // 2. Check if cache needs refresh
      const isExpired = !hasStaleCache || Date.now() - staleTs >= CACHE_TTL;

      if (!isExpired) {
        setLoading(false);
        return;
      }

      // 3. Fetch fresh data in background
      setLoading(true);
      try {
        await axios.get(configs.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

        const slug = 'navbar';
        const isDup = slug && slug.includes('-');

        let res;
        if (isDup) {
          res = await axios.get(`${configs.API_URL}/api/customsettings/${slug}`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true,
          });
        } else {
          res = await axios.get(configs.API_URL + '/api/nav-config', {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true,
          });
        }

        const dbData = isDup 
          ? (res.data.customSettings || {}) 
          : (res.data.navBarSettings || {});

        const freshConfig = {
          ...DEFAULT_CONFIG,
          ...dbData,
          topBar: { ...DEFAULT_CONFIG.topBar, ...dbData.topBar },
          logo: { ...DEFAULT_CONFIG.logo, ...dbData.logo },
          search: { ...DEFAULT_CONFIG.search, ...dbData.search },
          nav: { ...DEFAULT_CONFIG.nav, ...dbData.nav },
          icons: { ...DEFAULT_CONFIG.icons, ...dbData.icons },
          menuItems: dbData.menuItems?.map(i => ({ 
            ...i, 
            id: i.id || Math.random().toString(36).slice(2) 
          })) ?? DEFAULT_CONFIG.menuItems,
          menuPosition: dbData.menuPosition || "left",
        };

        setCfg(freshConfig);

        // Update cache with fresh data
        localStorage.setItem(CACHE_KEY_NAVBAR, JSON.stringify({
          data: freshConfig,
          ts: Date.now()
        }));

      } catch (err) {
        console.warn("Navbar API fetch failed - keeping cached data", err);
      } finally {
        setLoading(false);
      }
    };

    loadNavbar();
  }, [token, isPreview, previewConfig]);

  // ── Fetch categories ───────────────────────────────────────────────────────
  const fetchAllProductsAndCategories = async () => {
    if (isPreview) return;
    try {
      const [categoriesRes] = await Promise.all([
        axios.get(`${configs.API_URL}/api/categories_`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ]);

      const categoriesArray = categoriesRes.data?.newCategories2 || 
                             categoriesRes.data?.data || [];
      setCategories(['All', ...categoriesArray]);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  };

  useEffect(() => {
    fetchAllProductsAndCategories();
  }, [token, isPreview]);

  // ── Fetch user ─────────────────────────────────────────────────────────────
  const fetchUser = async () => {
    if (isPreview || !token) return;
    try {
      const res = await axios.get(`${configs.API_URL}/api/pullusers/${token}`);
      if (res.data.status !== 0) {
        setUser(res.data.user);
       // localStorage.setItem('userInfor', JSON.stringify(res.data.user));
      }
    } catch (error) {
     // console.error('Error fetching user:', error);
      //localStorage.removeItem('userInfor');
     // setUser(null);
    }
  };

  useEffect(() => {
    fetchUser();
    const interval = setInterval(fetchUser, 40000);
    return () => clearInterval(interval);
  }, [token, isPreview]);

  // ── Cart count ─────────────────────────────────────────────────────────────
  useEffect(() => {
    setCartCount(cartItems.length);
  }, [cartItems]);

  // ── Computed values ────────────────────────────────────────────────────────
  const leftItems  = effectiveConfig.menuItems.filter(m => m.position === "left");
  const rightItems = effectiveConfig.menuItems.filter(m => m.position === "right");

  const isDark = ["#000","#0f","#11","#1e"].some(p => effectiveConfig.nav.bg?.startsWith(p));
  const textColor = effectiveConfig.menuTextColor || (isDark ? "#fff" : "#374151");
  const iconColor = effectiveConfig.icons?.color || "#111827";
  const cartBadgeBg = effectiveConfig.cartBadgeColor || "#2563eb";
  let isLoggedIn = false;
     if(userInfo){
  isLoggedIn=true;
}

  const handleNavigate = (url) => {
    if (isPreview) return;
    setUserDropdownOpen(false);
    if (url === 'shop' || url.trim().toLowerCase() === 'shop') navigate(`/${url}/all`);
    else if (url === 'MainPage_s' || url === 'home' || url.trim().toLowerCase() === 'home') navigate(`/${url}`);
    else if (url === 'Orders') navigate(`/${url}`);
    else if (url === 'Authentication') {
      if (effectiveConfig.user?.sessionKey === token) navigate(`/Orders`);
      else navigate(`/${url}`);
    } else if (url === 'contact-us' || url.trim().toLowerCase() === 'contact us' || url.trim().toLowerCase() === 'contact') navigate(`/Contact`);
    else if (url === 'Account') navigate(`/myAccount`);
    else if (url === 'Wishlist') navigate(`/Wishlist`);
    else if (url.trim().toLowerCase() === 'help-center' || url.trim().toLowerCase() === 'help center' || url.trim().toLowerCase() === 'help') navigate(`/Help`);
    else if (url.trim().toLowerCase() === 'our-stores' || url.trim().toLowerCase() === 'stores') navigate(`/Stores`);
    else navigate(`/home`);
  };

  const logout = () => {
    if (isPreview) return;
    localStorage.removeItem('token');
    localStorage.removeItem('system_role');
    localStorage.removeItem('userInfor');
    setUser(null);
    setUserDropdownOpen(false);
    navigate('/home');
  };

  const handleSearch = (query) => {
    if (isPreview) return;
    
    setTimeout(function(){
      setFilteredProducts([]);
         search(query);
      },5000);
   
  };

  const search = async (query) => {
    if (isPreview) return;
    try {

      setLoading(true);
      await axios.get(configs.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const response = await axios.post(
        configs.API_URL + '/api/pull_products',
        { searchQuery: searchQuery, token: token },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      setLoading(false);
      setFilteredProducts(response.data.data || []);
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error("Search error:", error);
      setLoading(false);
    }
  };

  return (
    <>
      <header
        style={{
          position: isPreview ? "relative" : (effectiveConfig.nav.sticky ? "fixed" : "relative"),
          top: isPreview ? "auto" : (effectiveConfig.nav.sticky ? 0 : "auto"),
          zIndex: isPreview ? 10 : 40,
          width: "100%",
          background: effectiveConfig.nav.bg,
          borderBottom: effectiveConfig.nav.borderBottom
            ? `1px solid ${effectiveConfig.nav.borderColor || "transparent"}`
            : "none",
          transition: "all 0.3s ease",
          
        }}
      >
        {/* Top Bar */}
        {effectiveConfig.topBar.visible && (
          <div
            style={{
              padding: "6px 16px",
              background: `linear-gradient(to right, ${effectiveConfig.topBar.gradient[0]}, ${effectiveConfig.topBar.gradient[1]})`,
              color: "#fff",
              fontSize: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>✓ {effectiveConfig.topBar.leftMain}</span>
            <select
              style={{
                background: "transparent",
                border: "none",
                color: "inherit",
                fontSize: "inherit",
                cursor: "pointer",
              }}
            >
              {effectiveConfig.topBar.currencies.map((c, i) => (
                <option key={i} style={{ color: "#000" }}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Main Nav Bar */}
        <div
          style={{
            height: effectiveConfig.nav.height,
            padding: "0 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            maxWidth: 1400,
            margin: "0 auto",
          }}
        >
          {/* Logo */}
          <div style={{ flexShrink: 0 }} className="cursor-pointer" onClick={(e)=>navigate('/home')}>
            {effectiveConfig.logo.type === "image" && effectiveConfig.logo.imageUrl ? (
              <img
                src={effectiveConfig.logo.imageUrl}
                alt="Logo"
                style={{
                  marginTop:'-6px',
                  height: effectiveConfig.logo.height || 36,
                  width: effectiveConfig.logo.width,
                  objectFit: "contain",
                }}
              />
            ) : (
              <span
                style={{
                  fontSize: effectiveConfig.logo.fontSize * 0.8,
                  fontWeight: effectiveConfig.logo.fontWeight,
                  color: effectiveConfig.logo.color,
                  letterSpacing: "-0.5px",
                  whiteSpace: "nowrap",
                }}
              >
                {effectiveConfig.logo.text}
              </span>
            )}
          </div>

          {/* Desktop Menu + Search */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              flex: 1,
              justifyContent: "flex-end",
            }}
          >
            {/* Desktop Left items */}
            <nav
              style={{
                display: !isMobileView ? "flex" : "none",
                alignItems: "center",
                gap: 12,
                marginRight: "auto",
              }}
            >
              {leftItems.map((item) => (
                <a
                  key={item.id}
                  onClick={() => handleNavigate(item.href)}

                  className="cursor-pointer"
                  style={{
                    fontSize: effectiveConfig.menuFontSize || 14,
                    fontWeight: item.bold ? 700 : 500,
                    color: textColor,
                    textDecoration: "none",
                    padding: "8px 12px",
                    borderRadius: 6,
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) => !isPreview && (e.currentTarget.style.background = "rgba(0,0,0,0.05)")}
                  onMouseLeave={(e) => !isPreview && (e.currentTarget.style.background = "transparent")}
                >
                  {item.label}
                </a>
              ))}
            </nav>

            {/* Search (desktop) */}
            {effectiveConfig.search.visible && !isMobileView && (
              <div
              
                style={{
                  display: "flex",
                  alignItems: "center",
                  background: effectiveConfig.search.bgColor,
                  border: `1.5px solid ${effectiveConfig.search.borderColor}`,
                  borderRadius: effectiveConfig.search.borderRadius,
                  padding: "8px 12px",
                  minWidth: 220,
                  maxWidth: 360,
                  flex: 1,
                }}
              >
                <Icon name="search" size={16} color={effectiveConfig.search.iconColor} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {setSearchQuery(e.target.value); checkIfReady(e.target.value)}}
                 
                  placeholder={effectiveConfig.search.placeholder}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    marginLeft: 8,
                    flex: 1,
                    fontSize: 14,
                    color: "#1e293b",
                  }}
                 
                />
              </div>
            )}

            {/* Right items (desktop) */}
            {!isMobileView && (
              <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                {rightItems.map(m => (
                  <a
                    key={m.id}
                    onClick={() => handleNavigate(m.href)}
                    className="cursor-pointer"
                    style={{
                      fontSize: effectiveConfig.menuFontSize || 14,
                      fontWeight: m.bold ? 700 : 500,
                      color: textColor,
                      textDecoration: "none",
                      padding: "8px 12px",
                      borderRadius: 6,
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={(e) => !isPreview && (e.currentTarget.style.background = "rgba(0,0,0,0.05)")}
                    onMouseLeave={(e) => !isPreview && (e.currentTarget.style.background = "transparent")}
                  >
                    {m.label}
                  </a>
                ))}
              </div>
            )}

            {/* Icons + mobile hamburger */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {/* User dropdown */}
              <div className="relative" ref={userButtonRef}>
                <button
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 6 }}
                >
                  <Icon name="user" size={cfg.icons.size} color={iconColor} />
                </button>

                {userDropdownOpen && (
                  <div
                    ref={dropdownRef}
                    style={{
                      position: "absolute",
                      right: 0,
                      top: "100%",
                      marginTop: 8,
                      background: "#fff",
                      borderRadius: 8,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                      minWidth: 180,
                      zIndex: 1001,
                      overflow: "hidden",
                    }}
                  >
                    {isLoggedIn ? (
                      <>
                        <div style={{ padding: "12px 16px", borderBottom: "1px solid #eee", fontWeight: 600 }}>
                          Hello {cfg.user?.name || 'User'}!
                        </div>
                        <button onClick={() => { navigate('/Orders'); setUserDropdownOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 16px", border: "none", background: "none", cursor: "pointer" }}>
                          Orders
                        </button>
                        <button onClick={() => { navigate('/Wishlist'); setUserDropdownOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 16px", border: "none", background: "none", cursor: "pointer" }}>
                          Wishlist
                        </button>
                        <button onClick={() => { navigate('/myAccount'); setUserDropdownOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 16px", border: "none", background: "none", cursor: "pointer" }}>
                          My Account
                        </button>
                        <button
                          onClick={logout}
                          style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 16px", color: "#e53e3e", borderTop: "1px solid #eee", border: "none", background: "none", cursor: "pointer" }}
                        >
                          Logout
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => { navigate('/Authentication'); setUserDropdownOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 16px", border: "none", background: "none", cursor: "pointer" }}>
                          Login
                        </button>
                        <button onClick={() => { navigate('/Authentication'); setUserDropdownOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 16px", border: "none", background: "none", cursor: "pointer" }}>
                          Register
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              <button
                onClick={() => !isPreview && setCartOpen(true)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 8,
                  borderRadius: 8,
                  position: "relative",
                }}
              >
                <Icon name="bag" size={effectiveConfig.icons.size - 4} color={iconColor} />
                <span
                  style={{
                    position: "absolute",
                    top: -4,
                    right: -4,
                    background: cartBadgeBg,
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 700,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {_cartCount}
                </span>
              </button>

              {isMobileView && (
                <button
                  onClick={() => setMobileOpen(!mobileOpen)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 8,
                    borderRadius: 8,
                  }}
                >
                  <Icon
                    name={mobileOpen ? "x" : "menu"}
                    size={effectiveConfig.icons.size - 4}
                    color={iconColor}
                  />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile-only search bar */}
        {isMobileView && effectiveConfig.search.visible && (
          <div className="px-4 md:hidden" >
             <div
               
               style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 10px",
              background: effectiveConfig.search.bgColor,
              borderRadius: effectiveConfig.search.borderRadius,
              border: `1.5px solid ${effectiveConfig.search.borderColor}`,
              minWidth: 0
            }}
              >
               <Icon name="search" size={12} color={effectiveConfig.search.iconColor} />
                <input
                  type="text"
                  value={searchQuery}
                   onChange={(e) => {setSearchQuery(e.target.value); checkIfReady(e.target.value)}}
                
                  placeholder={effectiveConfig.search.placeholder}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    marginLeft: 8,
                    flex: 1,
                    fontSize: 14,
                    color: "#1e293b",
                  }}
                 
                />
              </div>
          </div>
         
        )}

        {/* Mobile Menu Dropdown */}
        {mobileOpen && isMobileView && (
          <div
            style={{
              background: effectiveConfig.nav.bg,
              borderTop: `1px solid ${effectiveConfig.nav.borderColor || "#e5e7eb"}`,
              padding: "12px 0",
            }}
          >
            {effectiveConfig.menuItems.map((item) => (
              <a
                key={item.id}
                onClick={() => { handleNavigate(item.href); setMobileOpen(false); }}
                style={{
                  display: "block",
                  padding: "12px 24px",
                  fontSize: effectiveConfig.menuFontSize || 16,
                  fontWeight: item.bold ? 700 : 500,
                  color: textColor,
                  textDecoration: "none",
                }}
              >
                {item.label}
              </a>
            ))}
          </div>
        )}
         <CategoriesNav configz={CatConfig}/>
      </header>
<br/><br/><br/><br/><br/><br/>
      {/* Overlays & BottomBar – only shown in real mode */}
      {!isPreview && (
        <>
          <SearchWidget
            isOpen={searchShow}
            onClose={() => {setSearchShow(false); setSearchQuery('')}}
            config={searchConfig}
            allCategories={allCategories}
            popularProducts={popularProducts}
            allCategorieX={filteredSuggestions}
            loading={loading}
            search_Query={searchQuery}
          />
          <CartSection
            isOpen={cartOpen}
            onClose={() => setCartOpen(false)}
            config={cartConfig}
          />
          <BottomBar />
        </>
      )}

    </>
  );
};

export default NavigationBar;